import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { Mail, Send, Check, AlertCircle } from 'lucide-react';

interface InviteFormData {
  email: string;
  role: 'super-admin' | 'club-admin';
}

const AdminInvitePortal: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<InviteFormData>({ email: '', role: 'club-admin' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sentInvites, setSentInvites] = useState<Array<{ email: string; role: string; timestamp: string }>>([]);

  // Redirect if not a super admin
  React.useEffect(() => {
    if (!user || (user.role !== 'SUPER_ADMIN' && user.role !== 'super_admin')) {
      navigate('/dashboard/student');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const endpoint = formData.role === 'super-admin' 
        ? '/api/admin/invite-super-admin'
        : '/api/admin/invite-club-admin';

      const response = await axios.post(
        `${BACKEND_URL}${endpoint}`,
        { email: formData.email },
        { 
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          } 
        }
      );

      if (response.data.success) {
        setSuccess(`Invitation sent to ${formData.email} for ${formData.role === 'super-admin' ? 'Super Admin' : 'Club Admin'} role`);
        setSentInvites([
          ...sentInvites,
          {
            email: formData.email,
            role: formData.role,
            timestamp: new Date().toLocaleString()
          }
        ]);
        setFormData({ email: '', role: 'club-admin' });
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="admin-invite-portal">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="invite-container"
        style={{
          maxWidth: '600px',
          margin: '40px auto',
          padding: '30px',
          backgroundColor: 'rgba(0, 20, 40, 0.8)',
          border: '2px solid #00ffff',
          borderRadius: '12px',
          boxShadow: '0 0 30px rgba(0, 255, 255, 0.3)'
        }}
      >
        <h1 style={{ 
          color: '#00ffff', 
          fontSize: '28px',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          Invite Admin
        </h1>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              backgroundColor: 'rgba(220, 53, 69, 0.2)',
              border: '1px solid #dc3545',
              color: '#ff6b6b',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <AlertCircle size={18} />
            {error}
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              backgroundColor: 'rgba(40, 167, 69, 0.2)',
              border: '1px solid #28a745',
              color: '#6fd66f',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <Check size={18} />
            {success}
          </motion.div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <div>
            <label style={{ color: '#00ffff', display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'super-admin' | 'club-admin' })}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'rgba(0, 255, 255, 0.1)',
                border: '2px solid #00ffff',
                color: '#00ffff',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <option value="club-admin">Club Admin</option>
              <option value="super-admin">Super Admin</option>
            </select>
          </div>

          <div>
            <label style={{ color: '#00ffff', display: 'block', marginBottom: '8px', fontSize: '14px' }}>
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={20} style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#00ffff' 
              }} />
              <input
                type="email"
                placeholder="admin@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={loading}
                required
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 45px',
                  backgroundColor: 'rgba(0, 255, 255, 0.1)',
                  border: '2px solid #00ffff',
                  color: '#00ffff',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'text',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              padding: '12px 24px',
              backgroundColor: '#00ffff',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
          >
            <Send size={18} />
            {loading ? 'Sending Invite...' : 'Send Invitation'}
          </motion.button>
        </form>

        {sentInvites.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginTop: '40px', borderTop: '2px solid rgba(0, 255, 255, 0.3)', paddingTop: '20px' }}
          >
            <h2 style={{ color: '#00ffff', fontSize: '18px', marginBottom: '15px' }}>Recent Invitations</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {sentInvites.map((invite, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  style={{
                    backgroundColor: 'rgba(0, 255, 255, 0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #00ffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <p style={{ color: '#00ffff', margin: '0', fontWeight: '600' }}>{invite.email}</p>
                    <p style={{ color: '#00ffff', margin: '4px 0 0 0', fontSize: '12px', opacity: 0.7 }}>
                      {invite.role === 'super-admin' ? 'Super Admin' : 'Club Admin'} • {invite.timestamp}
                    </p>
                  </div>
                  <Check size={18} style={{ color: '#6fd66f' }} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminInvitePortal;
